import { loadGMap } from './_load.map.js';

let geocoder;
let geolocation;
let permissionState;

/**
 *
 * @param {string} text - The search input text.
 * @returns {Promise} Autocomplete result or null on failure
 */
export async function getAutoComplete(text) {
    try{
        await loadGMap();

        const service = new window.google.maps.places.AutocompleteService();
        const predictions = await new Promise((resolve, reject) => {
            service.getPlacePredictions(
                {
                    input: text,
                    componentRestrictions: {
                        country: 'US',
                    },
                },
                (results, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                        resolve(results);
                    } else {
                        resolve(null);
                    }
                }
            );
        });

        if(!Array.isArray(predictions)) return null;

        const filtered =  predictions.reduce((prev, item) => {
            if (item && item.terms && item.terms.length > 4) {
                prev.push({
                    text: item.description,
                });
            }
            return prev;
        }, []);

        return filtered;
    } catch (error) {
        console.error('getAutoComplete error: ', error);
        return null;
    }
};

/**
 * Geocoding search using text query
 * @param {string} text - Address or place name to search.
 * @returns {Promise} - Geocoder result or null if failure.
 */
export async function geoServiceSearch(text) {
    try {
        await loadGMap();
        geocoder = geocoder || new window.google.maps.Geocoder();
        const results = new Promise(resolve => {
            geocoder.geocode(
                {
                    address: text,
                    componentRestrictions: {
                        country: 'US',
                    },
                },
                (results, status) => {
                    if (status === window.google.maps.GeocoderStatus.OK) {
                        resolve(results);
                    } else {
                        resolve(null);
                    }
                }
            );
        });

        return results;
    } catch (error) {
        console.error('geoServiceSearch error: ', error);
        return null;
    }
};

/**
 * Gets current geolocation using the browser API.
 * @returns {Promise} - Coordinates with lat and lng or null on failure.
 */
export function geoServiceLocation() {
    if(geolocation) {
        return Promise.resolve(geolocation);
    }

    return new Promise((resolve) => {
        if(!navigator.geolocation) {
            resolve(null);
            return;
        }
        window.navigator.geolocation.getCurrentPosition(
            (position) => {
                if (position && position.coords) {
                    geolocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    resolve(geolocation, position);
                } else {
                    resolve(null);
                }
            },
            () => {
                resolve(null);
            }
        );
    });
};

/**
 * Gets shared geolocation with user permission checks.
 * @returns {Promise} - Coordinates with lat and lng or null on failure.
 */
export async function getSharedGeoLocation() {
    if(geolocation) return geolocation;

    try{
        if((!permissionState || permissionState === 'prompt') && navigator.permissions?.query) {
            const res = await navigator.permissions.query({ name: 'geolocation' });
            permissionState = res?.state;
            if(permissionState === 'granted') {
                return await geoServiceLocation();
            }
        }
    } catch (error) {
        console.error('getSharedGeoLocation error: ', error);
    }
    return null;
};
